import React, { useState, useEffect } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { FaEye, FaSearch } from "react-icons/fa";
import PDFViewerModal from "../common/PDFViewerModal";
import { getInvoiceCargoDocumentsStatus } from "../../services/OCRDataService";
import { viewDoc } from "../../services/dataService";
import "./UploadedFilesTable.css";

const UploadedFilesList = () => {
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const today = new Date();
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(today.getDate() - 15);
    const formatDate = (date) => date.toISOString().split("T")[0];
    setStartDate(formatDate(fifteenDaysAgo));
    setEndDate(formatDate(today));
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchInvoices(startDate, endDate);
    }
  }, [startDate, endDate]);

  const fetchInvoices = async (start, end) => {
    try {
      const adjustedEndDate = new Date(end);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
      const formattedEndDate = adjustedEndDate.toISOString().split("T")[0];

      const response = await getInvoiceCargoDocumentsStatus(start, formattedEndDate);
      setTableData(response.data || []);
    } catch (error) {
      console.error("Failed to fetch invoices", error);
      setTableData([]);
    }
  };

  useEffect(() => {
    handleSearch(searchTerm, tableData);
    setCurrentPage(1); // Reset to first page when data changes
  }, [searchTerm, tableData]);

  const handleSearch = (term, source = tableData) => {
    let filtered = [...source];
    if (term) {
      filtered = filtered.filter((doc) =>
        doc.InvoiceNumber?.toLowerCase().includes(term.toLowerCase())
      );
    }

    // Sort by SendToKlearStackDate in descending order (latest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.SendToKlearStackDate);
      const dateB = new Date(b.SendToKlearStackDate);
      return dateB - dateA;
    });

    setFilteredData(filtered);
  };

  const viewPDFHandler = async (documentName) => {
    try {
      const response = await viewDoc(documentName);
      if (response?.data?.Message) {
        setSelectedPDF(response.data.Message);
        setShowPDFModal(true);
      } else {
        console.error("Document not found:", documentName);
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const getStatusSymbol = (doc) => {
    const passed = doc.FailedToSendKlareStack === 0 && doc.FailedToReceivedAtVizio === 0;
    return (
      <span style={{ color: passed ? "green" : "red", fontWeight: "bold" }}>
        {passed ? "✔" : "❌"}
      </span>
    );
  };

  const toggleInvoice = (invoiceNumber) => {
    setExpandedInvoices((prev) => ({
      ...prev,
      [invoiceNumber]: !prev[invoiceNumber],
    }));
  };

  const groupedData = filteredData.reduce((acc, doc) => {
    if (!doc.InvoiceNumber) return acc;
    if (!acc[doc.InvoiceNumber]) {
      acc[doc.InvoiceNumber] = [];
    }
    acc[doc.InvoiceNumber].push(doc);
    return acc;
  }, {});

  const groupedEntries = Object.entries(groupedData)
    .sort(([, aDocs], [, bDocs]) => {
      const dateA = new Date(aDocs[0]?.SendToKlearStackDate || 0);
      const dateB = new Date(bDocs[0]?.SendToKlearStackDate || 0);
      return dateB - dateA;
    });

  const totalPages = Math.ceil(groupedEntries.length / itemsPerPage);
  const currentEntries = groupedEntries.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="upload-container">
      <div className="top-bar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h4 className="m-0 flex-grow-1">Uploaded Documents</h4>

        <InputGroup style={{ maxWidth: "400px", minWidth: "280px" }}>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by Invoice Number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ height: "45px", fontSize: "16px" }}
          />
        </InputGroup>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span>From:</span>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="custom-input"
            style={{ maxWidth: "160px" }}
          />
          <span>To:</span>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="custom-input"
            style={{ maxWidth: "160px" }}
          />
        </div>
      </div>

      <table className="invoice-table" style={{ width: "100%", backgroundColor: "#fff", borderCollapse: "collapse" }}>
        <thead style={{ backgroundColor: "#f0f0f0" }}>
          <tr>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>Invoice Number</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>Uploaded Date</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>Uploaded By</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentEntries.map(([invoiceNumber, docs]) => {
            const isExpanded = expandedInvoices[invoiceNumber];
            return (
              <React.Fragment key={invoiceNumber}>
                <tr style={{ backgroundColor: "#f9f9f9" }}>
                  <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>{invoiceNumber}</td>
                  <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>
                    {docs[0]?.SendToKlearStackDate ? (() => {
                      const d = new Date(docs[0].SendToKlearStackDate);
                      const pad = (n) => n.toString().padStart(2, '0');
                      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                    })() : "N/A"}
                  </td>
                  <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>{docs[0]?.UploadBY ? docs[0].UploadBY.charAt(0).toUpperCase() + docs[0].UploadBY.slice(1) : "N/A"}
</td>
                  <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center" }}>
                    <Button size="sm" variant="link" onClick={() => toggleInvoice(invoiceNumber)}>
                      <FaEye /> {isExpanded ? "Hide" : "View"}
                    </Button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan="4" style={{ padding: 0 }}>
                      <table style={{ width: "100%", backgroundColor: "#fafafa", borderCollapse: "collapse" }}>
                        <thead style={{ backgroundColor: "#eee" }}>
                          <tr>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>Document Name</th>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>Document Type</th>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>Sent to Klearstack</th>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>Received at Vizio</th>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>Status</th>
                            <th style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>View</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((doc) => (
                            <tr key={doc.InvoiceDocId}>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>{doc.DocumentName}</td>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>{doc.Document_type}</td>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>
                                {doc.SendToKlearStackDate ? (() => {
                                  const d = new Date(doc.SendToKlearStackDate);
                                  const pad = (n) => n.toString().padStart(2, '0');
                                  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                                })() : "N/A"}
                              </td>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>
                                {doc.RecivedAtVizioDate ? (() => {
                                  const d = new Date(doc.RecivedAtVizioDate);
                                  d.setMinutes(d.getMinutes() + 330); // Offset
                                  const pad = (n) => n.toString().padStart(2, '0');
                                  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                                })() : "N/A"}
                              </td>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>
                                {getStatusSymbol(doc)}
                              </td>
                              <td style={{ padding: "6px", border: "1px solid #ccc", textAlign: "center" }}>
                                <Button size="sm" variant="link" onClick={() => viewPDFHandler(doc.DocumentName)}>
                                  <FaEye /> View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3 gap-3">
          <Button
            size="sm"
            variant="info"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <span>Page {currentPage} of {totalPages}</span>
          <Button
            size="sm"
            variant="info"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <PDFViewerModal show={showPDFModal} onHide={() => setShowPDFModal(false)} fileUrl={selectedPDF} />
    </div>
  );
};

export default UploadedFilesList;
